import type {
  Identity,
  PasswordVerdict,
  UsernameVerdict,
  VerificationState,
} from '../types/auth'

/**
 * A deterministic salted digest. This stands in for a real password-hashing
 * function (argon2/bcrypt on the server); the point preserved here is that
 * only a salted digest is ever stored, so a stored value cannot be read back
 * as the original secret.
 */
export function digest(value: string, salt: string): string {
  let a = 0x811c9dc5
  let b = 0x01000193
  const input = `${salt}::${value}`
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i)
    a = (a ^ code) * 0x01000193
    a >>>= 0
    b = (b + code * (i + 7)) * 31
    b >>>= 0
  }
  return `${a.toString(36)}${b.toString(36)}`.padEnd(16, '0').slice(0, 24)
}

export function makeSalt(): string {
  return Math.random().toString(36).slice(2, 12)
}

export function hashPassword(password: string): { hash: string; salt: string } {
  const salt = makeSalt()
  return { hash: digest(password, salt), salt }
}

export function verifyPassword(password: string, hash: string, salt: string): boolean {
  return digest(password, salt) === hash
}

/** Six digits, unpredictable enough for a short-lived one-time code. */
export function makeCode(): string {
  return String(Math.floor(100000 + Math.random() * 900000))
}

export function makeToken(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`
}

/* ---------------------------------------------------------------- usernames */

export const USERNAME_MIN = 3
export const USERNAME_MAX = 20

/**
 * Names that would let someone impersonate Campuzon or a staff function.
 * Administrator-configurable in the real system; the list is data, not logic.
 */
export const RESERVED_USERNAMES = [
  'campuzon',
  'campazon',
  'havazon',
  'admin',
  'administrator',
  'support',
  'help',
  'official',
  'security',
  'finance',
  'system',
  'root',
  'moderator',
  'staff',
  'team',
  'billing',
  'payments',
  'campagift',
  'verify',
  'verified',
  'representative',
  'noreply',
]

export function normaliseUsername(raw: string): string {
  return raw.trim().replace(/^@+/, '').toLowerCase()
}

export function judgeUsername(raw: string, takenKeys: string[]): UsernameVerdict {
  const key = normaliseUsername(raw)
  if (!key) {
    return { ok: false, kind: 'empty', message: 'Choose a username. This is how people find you.' }
  }
  if (key.length < USERNAME_MIN) {
    return {
      ok: false,
      kind: 'length',
      message: `Usernames need at least ${USERNAME_MIN} characters.`,
    }
  }
  if (key.length > USERNAME_MAX) {
    return {
      ok: false,
      kind: 'length',
      message: `Usernames can be at most ${USERNAME_MAX} characters.`,
    }
  }
  if (!/^[a-z0-9](?:[a-z0-9._]*[a-z0-9])?$/.test(key)) {
    return {
      ok: false,
      kind: 'format',
      message:
        'Use letters, numbers, full stops and underscores. Start and end with a letter or number.',
    }
  }
  if (/[._]{2,}/.test(key)) {
    return {
      ok: false,
      kind: 'format',
      message: 'Full stops and underscores cannot be repeated next to each other.',
    }
  }
  if (RESERVED_USERNAMES.includes(key) || /^campuzon/.test(key) || /(^|[._])admin([._]|$)/.test(key)) {
    return {
      ok: false,
      kind: 'reserved',
      message: 'This name is reserved so nobody can appear to speak for Campuzon.',
    }
  }
  if (takenKeys.includes(key)) {
    return { ok: false, kind: 'taken', message: `@${key} is already taken.` }
  }
  return { ok: true, kind: 'available', message: `@${key} is available.` }
}

/* ---------------------------------------------------------------- passwords */

/** Passwords seen so often that they offer no protection at all. */
const COMMON = [
  'password',
  'password1',
  'password123',
  '12345678',
  '123456789',
  'qwerty123',
  'iloveyou',
  'letmein',
  'campuzon',
  'campuzon1',
  'student1',
  'abc12345',
]

export function judgePassword(password: string, context: string[] = []): PasswordVerdict {
  const problems: string[] = []
  const guidance: string[] = []

  if (password.length < 10) {
    problems.push('Use at least 10 characters. Length protects you more than symbols do.')
  }
  if (COMMON.includes(password.toLowerCase())) {
    problems.push('This password appears on published breach lists. Choose something else.')
  }
  for (const item of context) {
    if (item && item.length > 2 && password.toLowerCase().includes(item.toLowerCase())) {
      problems.push('Do not include your name, username or email in your password.')
      break
    }
  }
  if (/^(.)\1+$/.test(password)) {
    problems.push('A single repeated character is not a password.')
  }

  let score = 0
  if (password.length >= 10) score += 1
  if (password.length >= 14) score += 1
  if (/[a-z]/.test(password) && /[A-Z0-9]/.test(password)) score += 1
  if (/[^a-zA-Z0-9]/.test(password) || password.length >= 18) score += 1
  if (problems.length > 0) score = Math.min(score, 1)

  if (password.length > 0 && password.length < 14) {
    guidance.push('Three or four unrelated words are easier to remember and harder to guess.')
  }

  const labels = ['Too weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const
  return {
    score: score as PasswordVerdict['score'],
    label: labels[score],
    problems,
    guidance,
  }
}

/* --------------------------------------------------------------- rate limits */

export interface Throttle {
  attempts: number
  lockedUntil: number | null
}

export const MAX_LOGIN_ATTEMPTS = 5
export const LOCK_SECONDS = 60

export function registerFailure(current: Throttle): Throttle {
  const attempts = current.attempts + 1
  if (attempts >= MAX_LOGIN_ATTEMPTS) {
    return { attempts, lockedUntil: Date.now() + LOCK_SECONDS * 1000 }
  }
  return { attempts, lockedUntil: null }
}

export function isLocked(current: Throttle): boolean {
  return current.lockedUntil !== null && Date.now() < current.lockedUntil
}

export function secondsRemaining(current: Throttle): number {
  if (current.lockedUntil === null) return 0
  return Math.max(0, Math.ceil((current.lockedUntil - Date.now()) / 1000))
}

/* ----------------------------------------------------------------- identity */

export function maskEmail(email: string): string {
  const [local, domain] = email.split('@')
  if (!domain) return email
  const head = local.slice(0, 1)
  const tail = local.length > 2 ? local.slice(-1) : ''
  return `${head}${'•'.repeat(Math.max(3, local.length - 2))}${tail}@${domain}`
}

export function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  if (digits.length < 4) return '•••'
  return `${'•'.repeat(Math.max(3, digits.length - 3))}${digits.slice(-3)}`
}

export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

/** Progress is DERIVED from what is actually done — never a stored number. */
export function onboardingProgress(identity: Identity): {
  percent: number
  done: number
  total: number
  next: string | null
} {
  const steps: Array<[boolean, string]> = [
    [identity.onboarding.usernameConfirmed, 'Confirm your username'],
    [identity.emailState === 'verified', 'Verify your email address'],
    [identity.onboarding.campusSelected, 'Choose your campus'],
    [identity.onboarding.programmeAdded, 'Add your programme'],
    [identity.onboarding.photoAdded, 'Add a profile photo'],
    [identity.onboarding.interestsAdded, 'Tell us what you are here for'],
    [identity.onboarding.notificationsSet, 'Set notification preferences'],
  ]
  const done = steps.filter(([complete]) => complete).length
  const next = steps.find(([complete]) => !complete)?.[1] ?? null
  return { percent: Math.round((done / steps.length) * 100), done, total: steps.length, next }
}

export function statusExplanation(identity: Identity): string | null {
  switch (identity.status) {
    case 'pending_verification':
      return 'Verify your email address to unlock transactions, listings and messaging.'
    case 'restricted':
      return identity.statusReason ?? 'Some actions are limited while we review your account.'
    case 'suspended':
      return identity.statusReason ?? 'This account is suspended. Contact support to appeal.'
    case 'deactivated':
      return 'This account is deactivated. Signing in again reactivates it.'
    default:
      return null
  }
}

export function verificationLabel(state: VerificationState): string {
  return state === 'verified' ? 'Verified' : state === 'pending' ? 'Awaiting code' : 'Not verified'
}

export function describeDevice(): string {
  if (typeof navigator === 'undefined') return 'Unknown device'
  const ua = navigator.userAgent
  const platform = /Android/i.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/i.test(ua)
      ? 'iOS'
      : /Mac/i.test(ua)
        ? 'macOS'
        : /Windows/i.test(ua)
          ? 'Windows'
          : 'Unknown platform'
  const browser = /Edg/i.test(ua)
    ? 'Edge'
    : /Chrome/i.test(ua)
      ? 'Chrome'
      : /Safari/i.test(ua)
        ? 'Safari'
        : /Firefox/i.test(ua)
          ? 'Firefox'
          : 'Browser'
  return `${browser} on ${platform}`
}
